import { useRef } from 'react'
import { cn } from '@playbooks/ui/lib/utils'
import { ViewMode } from '@playbooks/action-cards-utils/src'
import { Section } from '@playbooks/core/src/interfaces/ActionCard'
import { ResizablePanel, ResizablePanelGroup } from '@playbooks/ui/components/resizable'

import PinnedSections from './PinnedSections'
import SlidesContainer from './SlidesContainer'
import { SplitViewPosition } from '../types'
import { Scope } from '../utils/makeSeparatorScopes'
import { useAppContext } from '../contexts/AppContext'

export default function ActionCardsSections({
  splitViewPosition,
  viewMode = ViewMode.DEFAULT,
  isSplitViewEnabled = false,
  visiblePinnedSections,
  scopes,
  hideScore,
  lastSlideHtml,
}: {
  splitViewPosition: SplitViewPosition
  viewMode: ViewMode | undefined
  isSplitViewEnabled: boolean | undefined
  visiblePinnedSections: Section[]
  scopes: Scope[]
  hideScore: boolean
  lastSlideHtml: string | null | undefined
}) {
  const { onSplitViewResize } = useAppContext()

  const usersInteractionRef = useRef(false)
  const isRightSplit = splitViewPosition === 'right'
  const isFunnelView = viewMode === ViewMode.FUNNEL

  const isSplitView = isSplitViewEnabled && !isFunnelView

  const pinnedSectionsLeftEnabled = !isRightSplit && isSplitView
  const pinnedSectionsRightEnabled = isRightSplit && isSplitView

  const handleLayoutChanged = (event: { [id: string]: number }) => {
    if (!isSplitView) return
    // We need to check if the user is interacting with the slider
    // because the onLayoutChanged is called when the component is mounted
    // and provides not correct size values
    if (!usersInteractionRef.current) return

    const [leftSize, rightSize] = Object.values(event)

    const size = isRightSplit ? rightSize : leftSize

    // Save sizes to a storage
    onSplitViewResize?.(size)
  }

  return (
    <ResizablePanelGroup
      onLayoutChanged={handleLayoutChanged}
      onPointerDown={() => (usersInteractionRef.current = true)}
      // NOTE: onPointerUp is called always after onLayoutChanged but this is not explicit
      onPointerUp={() => (usersInteractionRef.current = false)}
      orientation="horizontal"
      data-role="data-action-cards-container"
      // overflow-visible! to make the slider to work in funnel view
      className={cn('relative flex w-full h-full gap-2', isFunnelView && 'overflow-visible!')}
    >
      <PinnedSections sections={visiblePinnedSections} enabled={!!pinnedSectionsLeftEnabled} isRight={isRightSplit} />
      {/* overflow-visible! to make the slider to work in funnel view */}
      <ResizablePanel minSize={200} className={cn(isFunnelView && 'overflow-visible!')}>
        <SlidesContainer
          hideScore={!!hideScore}
          isFunnelView={isFunnelView}
          scopes={scopes}
          lastSlideHtml={lastSlideHtml}
        />
      </ResizablePanel>
      <PinnedSections sections={visiblePinnedSections} enabled={!!pinnedSectionsRightEnabled} isRight={isRightSplit} />
    </ResizablePanelGroup>
  )
}
